import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { Box, Typography } from "@mui/material";
import { BlockOffsStyled } from "./blockoffs.styles";
import { Button } from "../../../../components/button/button";
import BlockoffCard from "../../components/blockoff-card/blockoff-card";
import { isSomething } from "../../../../utils/common-utils";
import DeleteBlockOffModal from "./delete-blockoff-modal/delete-blockoff-modal";
import AddBlockOffModal from "./add-blockoff-modal/add-blockoff-modal";
import { AddIcon } from "../../../../assets/svgComponents";
import ReusableTabs from "../../../../components/tabs/tabs";
import { DateRangePickerField } from "../../../../components/form-fields/date-range-picker/date-range-picker";
import { getAllBlocksOff } from "../../../../actions";
import { parsedBlocksOffList } from "./blockoffs.utils";
import Loader from "../../../../components/loader/loader";
import { addNotification } from "../../../../store/slices/ui-slice";
import { useAppDispatch } from "../../../../store/hooks";
import { v4 as uuid } from "uuid";
import { ProviderResponse } from "../../../../types/common-types";
import DetailBlockOffModal from "./detail-blockoff-modal/detail-blockoff-modal";

const VetContent = ({
  loading,
  blockoffs,
  setDeleteModalItem,
  setShowDetailModal,
}: any) => {
  const handleView = (item: any) => setShowDetailModal(item);
  const handleDelete = (item: any) => setDeleteModalItem(item);

  if (loading) return <Loader absolute />;
  if (!blockoffs || blockoffs.length === 0) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100%"
        sx={{
          minHeight: "400px",
        }}
      >
        <Typography variant="body1" sx={{ textAlign: "center" }}>
          No blockoffs available for the selected Month.
        </Typography>
      </Box>
    );
  }

  return (
    <Box className="card-wrapper" style={{ height: "100%", width: "100%" }}>
      {blockoffs?.map((blockoff: any, index: number) => {
        return (
          <BlockoffCard
            key={uuid()}
            blockoff={blockoff}
            handleView={handleView}
            handleDelete={handleDelete}
          />
        );
      })}
    </Box>
  );
};

const BlockOffs = ({
  providersAppointmentData,
}: {
  providersAppointmentData: ProviderResponse[];
}) => {
  const dispatch = useAppDispatch();

  const [deleteModalItem, setDeleteModalItem] = useState({});
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(null);
  const [activeTab, setActiveTab] = useState<string | null>(
    providersAppointmentData?.[0]?.display_name || null
  );
  const [selectedDate, setSelectedDate] = useState(dayjs().startOf("month"));
  const [blockoffs, setBlockoffs] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  const handleDateChange = async (newValue: any) => {
    if (!newValue) return;

    setLoading(true);
    setBlockoffs(null);

    const startOfMonth = dayjs(newValue).startOf("month").format("YYYY-MM-DD");
    const endOfMonth = dayjs(newValue).endOf("month").format("YYYY-MM-DD");

    const payload = {
      when: startOfMonth,
      until: endOfMonth,
      provider:
        providersAppointmentData?.find(
          (x: any) => x?.display_name === activeTab
        )?.actor_id || "",
    };

    try {
      const response = await getAllBlocksOff(payload);
      const data = parsedBlocksOffList(response?.data?.data, newValue);
      setBlockoffs(data || []);
    } catch (error: any) {
      dispatch(
        addNotification({
          id: uuid(),
          type: "error",
          message: error.response?.data?.message || "Failed to fetch users",
        })
      );
    } finally {
      setLoading(false);
    }

    setSelectedDate(newValue);
  };

  useEffect(() => {
    if (selectedDate) handleDateChange(selectedDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const tabs = useMemo(() => {
    if (!providersAppointmentData) return [];

    return providersAppointmentData.map((x: any) => {
      let content = null;

      if (x?.display_name === "Tech Appointments") {
        content = (
          <VetContent
            blockoffs={blockoffs}
            setDeleteModalItem={setDeleteModalItem}
            setShowDetailModal={setShowDetailModal}
            loading={loading}
          />
        );
      } else {
        content = (
          <VetContent
            blockoffs={blockoffs}
            setDeleteModalItem={setDeleteModalItem}
            setShowDetailModal={setShowDetailModal}
            loading={loading}
          />
        );
      }

      return {
        label: x?.display_name,
        content: content,
      };
    });
  }, [providersAppointmentData, blockoffs, loading]);

  return (
    <BlockOffsStyled>
      <Box className="header-wrapper">
        <Typography variant="h2">Blockoffs</Typography>
        <Box className="btn-wrapper">
          <DateRangePickerField
            value={selectedDate}
            onChange={handleDateChange}
            views={["month"]}
          />
          <Button className="text-btn" onClick={() => setShowAddModal(true)}>
            Add Blockoff
          </Button>
          <Button
            className="icon-btn"
            startIcon={<AddIcon />}
            onClick={() => setShowAddModal(true)}
          />
        </Box>
      </Box>
      <Box className="main-wrapper">
        <ReusableTabs tabs={tabs} onTabChange={setActiveTab} />
      </Box>

      {isSomething(deleteModalItem) && (
        <DeleteBlockOffModal
          item={deleteModalItem}
          onClose={() => setDeleteModalItem({})}
          updateBlockoffs={() => handleDateChange(selectedDate)}
        />
      )}

      {showAddModal && (
        <AddBlockOffModal
          selectedDate={selectedDate}
          onClose={() => setShowAddModal(false)}
          updateBlockoffs={() => handleDateChange(selectedDate)}
          providersAppointmentData={providersAppointmentData}
        />
      )}

      {showDetailModal && (
        <DetailBlockOffModal
          item={showDetailModal}
          onClose={() => setShowDetailModal(null)}
        />
      )}
    </BlockOffsStyled>
  );
};

export default BlockOffs;
